"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Public guide (ADR-026): /guides/save-your-day. Tells the "one meal is
 * data, not failure" story that mirrors Amy's OFF-PLAN MEALS (SAVE THE
 * DAY) system-prompt section (patterns/bitebalance-agent/system_prompt.py)
 * — the myth of the ruined day, why the goal is a weekly number, what Amy
 * does step by step, and a qualitative (never personalized) worked
 * example, ending in a gentle sign-up CTA.
 *
 * Standalone route: no auth, no personal context, no dependency on the
 * landing page's components (owned by another agent, who links here from
 * the marketing site). Copy lives in packages/i18n's guides.en.ts/
 * guides.de.ts, merged into the shared t() dictionary the same way
 * landingPage copy is.
 */

import { useEffect } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { LanguageToggle } from "@/components/layout/LanguageToggle"
import { useI18n } from "@/i18n"
import { PageEntrance, ReadingProgress } from "@/components/PageEntrance"

export default function SaveYourDayPage() {
  const { t } = useI18n()

  useEffect(() => {
    const previousTitle = document.title
    document.title = t("guides.saveYourDay.meta.title")

    const meta = document.querySelector('meta[name="description"]')
    const previousDescription = meta?.getAttribute("content") ?? null
    meta?.setAttribute("content", t("guides.saveYourDay.meta.description"))

    return () => {
      document.title = previousTitle
      if (previousDescription !== null) meta?.setAttribute("content", previousDescription)
    }
  }, [t])

  const amySteps = [
    {
      title: t("guides.saveYourDay.whatAmyDoes.step1Title"),
      body: t("guides.saveYourDay.whatAmyDoes.step1Body"),
    },
    {
      title: t("guides.saveYourDay.whatAmyDoes.step2Title"),
      body: t("guides.saveYourDay.whatAmyDoes.step2Body"),
    },
    {
      title: t("guides.saveYourDay.whatAmyDoes.step3Title"),
      body: t("guides.saveYourDay.whatAmyDoes.step3Body"),
    },
    {
      title: t("guides.saveYourDay.whatAmyDoes.step4Title"),
      body: t("guides.saveYourDay.whatAmyDoes.step4Body"),
    },
    {
      title: t("guides.saveYourDay.whatAmyDoes.step5Title"),
      body: t("guides.saveYourDay.whatAmyDoes.step5Body"),
    },
  ]

  const goalCards = [
    {
      title: t("guides.saveYourDay.goalTypes.lossTitle"),
      body: t("guides.saveYourDay.goalTypes.lossBody"),
    },
    {
      title: t("guides.saveYourDay.goalTypes.gainTitle"),
      body: t("guides.saveYourDay.goalTypes.gainBody"),
    },
    {
      title: t("guides.saveYourDay.goalTypes.maintainTitle"),
      body: t("guides.saveYourDay.goalTypes.maintainBody"),
    },
  ]

  const exampleSteps = [
    t("guides.saveYourDay.example.stepAmy1"),
    t("guides.saveYourDay.example.stepAmy2"),
    t("guides.saveYourDay.example.stepAmy3"),
    t("guides.saveYourDay.example.stepAmy4"),
    t("guides.saveYourDay.example.stepAmy5"),
  ]

  return (
    <PageEntrance>
      <ReadingProgress />
      <div className="min-h-screen bg-cream text-ink">
        <a
          href="#main"
          className="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:top-2 focus-visible:left-2 focus-visible:z-50 focus-visible:rounded-bb-md focus-visible:bg-paper focus-visible:px-4 focus-visible:py-2 focus-visible:text-ink focus-visible:shadow-[var(--bb-shadow)]"
        >
          {t("nav.skipToContent")}
        </a>

        <header className="border-b border-line bg-paper">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 md:px-8">
            <Link to="/" className="text-sm font-semibold text-ink-2 hover:text-ink">
              ← {t("guides.saveYourDay.backToHome")}
            </Link>
            <LanguageToggle />
          </div>
        </header>

        <main id="main">
          <article className="mx-auto max-w-3xl px-4 py-14 md:px-8 md:py-20">
            {/* Hero */}
            <p className="text-sm font-semibold tracking-wide text-green uppercase">
              {t("guides.saveYourDay.eyebrow")}
            </p>
            <h1 className="mt-3 text-4xl leading-tight font-black text-ink md:text-5xl">
              {t("guides.saveYourDay.hero.title")}
            </h1>
            <p className="mt-5 max-w-prose text-lg text-ink-2">
              {t("guides.saveYourDay.hero.subtitle")}
            </p>
            <p className="mt-3 text-sm font-medium text-ink-2">
              {t("guides.saveYourDay.hero.readTime")}
            </p>

            {/* Hook */}
            <section className="mt-12">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">
                {t("guides.saveYourDay.hook.title")}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.saveYourDay.hook.body1")}
              </p>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.saveYourDay.hook.body2")}
              </p>
              <p className="mt-4 text-base font-semibold text-ink">
                {t("guides.saveYourDay.hook.body3")}
              </p>
            </section>

            {/* Myth */}
            <section className="mt-12">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">
                {t("guides.saveYourDay.myth.title")}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.saveYourDay.myth.body1")}
              </p>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.saveYourDay.myth.body2")}
              </p>
              <blockquote className="mt-6 rounded-bb-lg border-l-4 border-green bg-paper p-5 text-lg font-medium text-ink shadow-[var(--bb-shadow)]">
                {t("guides.saveYourDay.myth.quoteBody")}
              </blockquote>
            </section>

            {/* How it works */}
            <section className="mt-12">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">
                {t("guides.saveYourDay.howItWorks.title")}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.saveYourDay.howItWorks.body1")}
              </p>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.saveYourDay.howItWorks.body2")}
              </p>

              <h3 className="mt-6 text-lg font-semibold text-ink">
                {t("guides.saveYourDay.howItWorks.listTitle")}
              </h3>
              <ul className="mt-3 flex flex-col gap-2">
                {[
                  t("guides.saveYourDay.howItWorks.listItem1"),
                  t("guides.saveYourDay.howItWorks.listItem2"),
                  t("guides.saveYourDay.howItWorks.listItem3"),
                  t("guides.saveYourDay.howItWorks.listItem4"),
                ].map(item => (
                  <li key={item} className="flex gap-3 text-base text-ink-2">
                    <span aria-hidden="true" className="mt-1 shrink-0 text-green">
                      ✓
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* What Amy does */}
            <section className="mt-14 rounded-bb-xl border border-line bg-paper p-6 shadow-[var(--bb-shadow)] md:p-8">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">
                {t("guides.saveYourDay.whatAmyDoes.title")}
              </h2>
              <p className="mt-3 text-base text-ink-2">
                {t("guides.saveYourDay.whatAmyDoes.intro")}
              </p>

              <ol className="mt-6 flex flex-col gap-6">
                {amySteps.map(step => (
                  <li key={step.title}>
                    <h3 className="text-lg font-semibold text-ink">{step.title}</h3>
                    <p className="mt-1 text-base text-ink-2">{step.body}</p>
                  </li>
                ))}
              </ol>
            </section>

            {/* Goal types */}
            <section className="mt-14">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">
                {t("guides.saveYourDay.goalTypes.title")}
              </h2>
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {goalCards.map(card => (
                  <div
                    key={card.title}
                    className="rounded-bb-lg border border-line bg-paper p-5 shadow-[var(--bb-shadow)]"
                  >
                    <h3 className="text-base font-semibold text-ink">{card.title}</h3>
                    <p className="mt-2 text-sm text-ink-2">{card.body}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Worked example */}
            <section className="mt-14">
              <span className="w-fit rounded-bb-full bg-lime/60 px-3 py-1 text-xs font-semibold tracking-wide text-ink uppercase">
                {t("guides.saveYourDay.example.introLabel")}
              </span>
              <h2 className="mt-3 text-2xl font-bold text-ink md:text-3xl">
                {t("guides.saveYourDay.example.title")}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.saveYourDay.example.intro")}
              </p>

              <div className="mt-6 rounded-bb-xl border border-line bg-paper p-6 shadow-[var(--bb-shadow)] md:p-8">
                <h3 className="text-lg font-semibold text-ink">
                  {t("guides.saveYourDay.example.scenarioTitle")}
                </h3>
                <p className="mt-2 text-base text-ink-2">
                  {t("guides.saveYourDay.example.scenarioBody")}
                </p>

                <h3 className="mt-6 text-lg font-semibold text-ink">
                  {t("guides.saveYourDay.example.stepAmyLabel")}
                </h3>
                <ol className="mt-3 flex flex-col gap-3">
                  {exampleSteps.map((step, i) => (
                    <li key={step} className="flex gap-3 text-base text-ink-2">
                      <span
                        aria-hidden="true"
                        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-lime/60 text-xs font-bold text-ink"
                      >
                        {i + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>

                <h3 className="mt-6 text-lg font-semibold text-ink">
                  {t("guides.saveYourDay.example.outcomeTitle")}
                </h3>
                <p className="mt-2 text-base text-ink-2">
                  {t("guides.saveYourDay.example.outcomeBody")}
                </p>
              </div>
            </section>

            {/* CTA */}
            <section className="mt-16 rounded-bb-xl border border-line bg-paper p-8 text-center shadow-[var(--bb-shadow)]">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">
                {t("guides.saveYourDay.cta.title")}
              </h2>
              <p className="mx-auto mt-3 max-w-prose text-base text-ink-2">
                {t("guides.saveYourDay.cta.body")}
              </p>
              <Button type="button" size="lg" className="mt-6 min-h-11" asChild>
                <Link to="/">
                  {t("guides.saveYourDay.cta.button")}
                  <span aria-hidden="true">→</span>
                </Link>
              </Button>
            </section>

            {/* Disclaimer */}
            <p className="mt-10 text-sm text-ink-2">{t("guides.saveYourDay.disclaimer")}</p>
          </article>
        </main>
      </div>
    </PageEntrance>
  )
}
